import { Progress } from "@/components/ui/progress";
import { cn } from "@/lib/utils";

import type { GoalSummary } from "../api";
import { ACCENT_BAR, ACCENT_TEXT, formatPct } from "../constants";

/** Compact, clickable goal tile for the "all goals" scoreboard beneath the
 *  flow story — adds the progress + linked-objective count the flow boxes
 *  don't show. */
export function GoalCard({
  goal,
  onClick,
  selected,
}: {
  goal: GoalSummary;
  onClick: () => void;
  selected?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={selected}
      className={cn(
        "group flex flex-col overflow-hidden rounded-lg border border-border bg-card text-left shadow-sm transition hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
        selected && "ring-2 ring-ring",
      )}
    >
      <span className={cn("h-1.5 w-full", ACCENT_BAR[goal.accent])} aria-hidden="true" />
      <span className="flex flex-1 flex-col gap-2 p-4">
        <span className="flex items-baseline justify-between gap-2">
          <span className={cn("text-sm font-bold leading-tight", ACCENT_TEXT[goal.accent])}>
            {goal.short_label || goal.title}
          </span>
          <span className="text-xs font-semibold tabular-nums text-muted-foreground">
            {formatPct(goal.progress_pct)}
          </span>
        </span>
        {goal.tagline ? (
          <span className="text-xs leading-snug text-muted-foreground">{goal.tagline}</span>
        ) : null}
        <Progress value={goal.progress_pct} className="mt-auto h-1.5" />
        <span className="text-[11px] text-muted-foreground">
          {goal.objective_count} {goal.objective_count === 1 ? "objective" : "objectives"} linked
        </span>
      </span>
    </button>
  );
}
